import { useState, useEffect } from "react";
import { CheckCircle, XCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default lead stages (fallback if no pipeline stages configured)
const DEFAULT_STAGES = [
  { key: "new", label: "New", shortLabel: "New", color: "bg-slate-500" },
  { key: "contacted", label: "Contacted", shortLabel: "Contact", color: "bg-blue-500" },
  { key: "interested", label: "Interested", shortLabel: "Interest", color: "bg-cyan-500" },
  { key: "qualified", label: "Qualified", shortLabel: "Qualify", color: "bg-emerald-500" },
  { key: "proposal", label: "Proposal Sent", shortLabel: "Proposal", color: "bg-purple-500" },
  { key: "negotiation", label: "Negotiation", shortLabel: "Negotiate", color: "bg-amber-500" },
  { key: "won", label: "Won", shortLabel: "Won", color: "bg-green-600" },
  { key: "lost", label: "Lost", shortLabel: "Lost", color: "bg-red-500" }
];

export { DEFAULT_STAGES as LEAD_STAGES };

// Match a lead's stage value against a stage definition (handles mixed casing/labels)
function matchesStage(leadStage, stagedef) {
  if (!leadStage) return false;
  const ls = leadStage.toLowerCase().trim();
  return ls === stagedef.key.toLowerCase() ||
    ls === stagedef.label.toLowerCase() ||
    (stagedef.shortLabel && ls === stagedef.shortLabel.toLowerCase()) ||
    // Handle "New Lead" matching "new"
    ls === "new lead" && stagedef.key === "new" ||
    ls === "proposal sent" && stagedef.key === "proposal" ||
    ls === "won / converted" && stagedef.key === "won";
}

export default function LeadStageProgress({ lead, onStageClick }) {
  const [stages, setStages] = useState(DEFAULT_STAGES);

  // Fetch business-specific pipeline stages
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/pipeline-stages`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            const sorted = data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            setStages(sorted.map(s => ({
              key: s.key,
              label: s.label,
              shortLabel: s.label.length > 8 ? s.label.substring(0, 7) + "…" : s.label,
              color: s.color || "bg-slate-500"
            })));
          }
        }
      } catch (e) { /* use defaults */ }
    })();
  }, []);

  const currentStageIndex = stages.findIndex(s => matchesStage(lead.stage, s));

  const currentStage = stages.find(s => matchesStage(lead.stage, s));

  return (
    <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-4 lg:py-6">
      <Card className="shadow-sm">
        <CardHeader className="pb-3 lg:pb-4 px-4 lg:px-6">
          {/* Title row + dropdown (dropdown only on sm+ — mobile uses the tappable grid below) */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <CardTitle className="text-base lg:text-lg">Lead Stage</CardTitle>
              {/* Show current stage as a pill next to the title on mobile so it's always visible */}
              {currentStage && (
                <span
                  className={`sm:hidden inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white shadow-sm ${currentStage.color}`}
                >
                  <CheckCircle className="w-3 h-3" />
                  {currentStage.label}
                </span>
              )}
            </div>
            <Select
              value={currentStage?.key || lead.stage || ""}
              onValueChange={(val) => {
                const stage = stages.find(s => s.key === val);
                if (stage) onStageClick(stage);
              }}
            >
              <SelectTrigger className="hidden sm:flex w-36 lg:w-48 h-9 lg:h-10">
                <SelectValue placeholder={lead.stage || "Select stage"} />
              </SelectTrigger>
              <SelectContent>
                {stages.map(stage => (
                  <SelectItem key={stage.key} value={stage.key}>{stage.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="px-4 lg:px-6">
          {/* Desktop / tablet: horizontal progress bar */}
          <div className="hidden sm:flex items-center overflow-x-auto pb-2">
            {stages.map((stage, index) => {
              const isActive = index <= currentStageIndex && currentStageIndex >= 0;
              const isCurrent = matchesStage(lead.stage, stage);
              const isWon = stage.key === "won" && isCurrent;
              const isLost = stage.key === "lost" && isCurrent;

              return (
                <div key={stage.key} className="flex-1 min-w-[80px] lg:min-w-0 relative">
                  {index > 0 && (
                    <div
                      className={`absolute left-0 top-1/2 -translate-y-1/2 h-1 w-full -ml-1/2 ${
                        isActive ? "bg-green-500" : "bg-slate-200"
                      }`}
                      style={{ zIndex: 0 }}
                    />
                  )}

                  <button
                    onClick={() => onStageClick(stage)}
                    disabled={isCurrent}
                    className={`relative z-10 w-full py-2 lg:py-3 px-1 lg:px-2 text-center transition-all ${
                      isWon ? "bg-green-500 text-white" :
                      isLost ? "bg-red-500 text-white" :
                      isCurrent ? "bg-blue-500 text-white" :
                      isActive ? "bg-green-100 text-green-700 hover:bg-green-200" :
                      "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    } ${index === 0 ? "rounded-l-lg" : ""} ${index === stages.length - 1 ? "rounded-r-lg" : ""}`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      {(isActive || isCurrent) && !isLost && (
                        <CheckCircle className="w-3 h-3 lg:w-4 lg:h-4" />
                      )}
                      {isLost && <XCircle className="w-3 h-3 lg:w-4 lg:h-4" />}
                      <span className="text-xs font-medium hidden lg:inline">{stage.label}</span>
                      <span className="text-xs font-medium lg:hidden">{stage.shortLabel}</span>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Mobile: 2-column grid of FULL-label pills — every stage visible, no scroll, big tap targets */}
          <div className="sm:hidden">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Tap to move stage</p>
            <div className="grid grid-cols-2 gap-2">
              {stages.map((stage, index) => {
                const isActive = index <= currentStageIndex && currentStageIndex >= 0;
                const isCurrent = matchesStage(lead.stage, stage);
                const isWon = stage.key === "won" && isCurrent;
                const isLost = stage.key === "lost" && isCurrent;

                return (
                  <button
                    key={stage.key}
                    onClick={() => onStageClick(stage)}
                    disabled={isCurrent}
                    aria-pressed={isCurrent}
                    className={`relative flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-xs font-semibold leading-tight min-h-[44px] border transition-all text-center ${
                      isWon ? "bg-green-500 text-white border-green-500 shadow-sm" :
                      isLost ? "bg-red-500 text-white border-red-500 shadow-sm" :
                      isCurrent ? "bg-blue-600 text-white border-blue-600 shadow-sm" :
                      isActive ? "bg-emerald-50 text-emerald-700 border-emerald-200 active:bg-emerald-100" :
                      "bg-slate-50 text-slate-600 border-slate-200 active:bg-slate-100"
                    }`}
                    data-testid={`mobile-stage-${stage.key}`}
                  >
                    {(isActive || isCurrent) && !isLost && <CheckCircle className="w-3.5 h-3.5 shrink-0" />}
                    {isLost && <XCircle className="w-3.5 h-3.5 shrink-0" />}
                    <span className="break-words">{stage.label}</span>
                    {/* tiny step indicator at top-right of each pill */}
                    <span className={`absolute top-1 right-1.5 text-[9px] font-bold leading-none ${isCurrent || isWon || isLost ? "text-white/70" : "text-slate-300"}`}>
                      {index + 1}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
